import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { CalendarDays, Check, MapPin, Sparkles, Users, Zap } from "lucide-react";
import {
  eventTypeHook,
  eventTypeLabel,
  formatEventDate,
  isCommunityEvent,
  isPast,
  proximityLabel,
  seatsPill,
  type EventType,
  type SquadEvent,
} from "@/lib/events";
import { markdownPreview } from "@/lib/markdown";
import type { RsvpStatus } from "@/lib/rsvps.functions";

/**
 * The event fields the tile renders. SquadEvent satisfies this, and narrower
 * crew-side payloads (attendees view) can satisfy it too without carrying the
 * full public-event shape around.
 */
export type EventTileEvent = Pick<SquadEvent, "id" | "title" | "slug" | "starts_at"> & {
  subtitle?: string | null;
  description?: string | null;
  location?: string | null;
  ends_at?: string | null;
  event_type?: string | null;
  cover_image_url?: string | null;
  is_intro_session?: boolean | null;
  host_kind?: "squadflight" | "community" | null;
  capacity?: number | null;
};

export function EventMeta({ event }: { event: EventTileEvent }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
      <span className="inline-flex items-center gap-2">
        <CalendarDays className="size-4 shrink-0 text-primary" />
        {formatEventDate(event.starts_at, event.ends_at)}
      </span>
      {event.location && (
        <span className="inline-flex min-w-0 items-center gap-2">
          <MapPin className="size-4 shrink-0 text-primary" />
          <span className="truncate">{event.location}</span>
        </span>
      )}
    </div>
  );
}

/** Says who's putting the night on — community pilot or SquadFlight. */
export function HostBadge({ event }: { event: EventTileEvent }) {
  const community = isCommunityEvent(event);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-display text-[11px] uppercase tracking-[0.15em] ${
        community
          ? "border-accent/50 bg-accent/10 text-accent"
          : "border-primary/50 bg-primary/10 text-primary"
      }`}
    >
      <Users className="size-3.5" />
      {community ? "Community hosted" : "SquadFlight"}
    </span>
  );
}

/** The kind of night, so the list scans without opening anything. */
export function TypeChip({ event }: { event: EventTileEvent }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-secondary px-3 py-1 font-display text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
      {eventTypeLabel(event.event_type ?? null)}
    </span>
  );
}

export function IntroChip() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-display text-[11px] uppercase tracking-[0.15em] text-primary">
      <Sparkles className="size-3.5" /> No gear or experience needed
    </span>
  );
}

/** "Tonight", "Tomorrow", "In 9 days" — urgency, not decoration. */
export function WhenPill({ event }: { event: EventTileEvent }) {
  const label = proximityLabel(event.starts_at);
  if (!label) return null;
  const soon = /tonight|tomorrow|this /i.test(label);
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 font-display text-[11px] font-semibold uppercase tracking-[0.15em] ${
        soon ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
      }`}
    >
      {label}
    </span>
  );
}

export function SeatsPill({ event, going }: { event: EventTileEvent; going: number | undefined }) {
  const pill = seatsPill(event.capacity ?? undefined, going);
  if (!pill) return null;
  const tone =
    pill.tone === "hot"
      ? "border-destructive/50 bg-destructive/10 text-destructive"
      : pill.tone === "warm"
        ? "border-accent/50 bg-accent/10 text-accent"
        : "border-border bg-secondary text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 font-display text-[11px] uppercase tracking-[0.15em] ${tone}`}
    >
      {pill.text}
    </span>
  );
}

export function MyRsvpPill({ status }: { status: RsvpStatus }) {
  const label =
    status === "going" || status === "attended"
      ? "You're in"
      : status === "waitlisted"
        ? "You're waitlisted"
        : status === "maybe"
          ? "You said maybe"
          : status === "cancelled"
            ? "Your RSVP was cancelled"
            : status === "no_show"
              ? "Past RSVP"
              : null;
  if (!label) return null;
  const active = status === "going" || status === "attended";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-display text-[11px] uppercase tracking-[0.15em] ${
        active
          ? "border-primary/50 bg-primary/10 text-primary"
          : "border-border bg-secondary text-muted-foreground"
      }`}
    >
      {active && <Check className="size-3.5" />}
      {label}
    </span>
  );
}

/** Listing shows a taste of the description; the RSVP page has the whole thing. */
export function DescriptionPreview({
  event,
  max,
  className,
  onOpen,
}: {
  event: EventTileEvent;
  max: number;
  className: string;
  onOpen?: ((from: string) => void) | undefined;
}) {
  if (!event.description) return null;
  const { text, truncated } = markdownPreview(event.description, max);
  if (!text) return null;
  return (
    <p className={className}>
      {text}{" "}
      {truncated && (
        <Link
          to="/events/$slug"
          params={{ slug: event.slug }}
          className="whitespace-nowrap text-primary underline underline-offset-4 hover:text-foreground"
          onClick={() => onOpen?.("read_more")}
        >
          Read the full details
        </Link>
      )}
    </p>
  );
}

/**
 * The event tile used across the public events list and reused on crew
 * screens (attendees view) so every surface describes the night the same way.
 * `cta` is the action slot — the public list passes its RSVP button; crew
 * pages can omit it entirely. Clicks on the tile call `onOpen` so the public
 * list can keep its telemetry and crew pages can stay silent.
 */
export function EventTile({
  event,
  going,
  position,
  myStatus,
  cta,
  onOpen,
  descriptionMax = 120,
  descriptionClassName = "mt-3 text-sm leading-relaxed text-muted-foreground",
}: {
  event: EventTileEvent;
  going?: number | undefined;
  position?: number | undefined;
  myStatus?: RsvpStatus | undefined;
  cta?: ReactNode;
  onOpen?: ((from: string) => void) | undefined;
  descriptionMax?: number;
  descriptionClassName?: string;
}) {
  const upcoming = !isPast({
    starts_at: event.starts_at,
    ends_at: event.ends_at ?? null,
  });
  return (
    <article className="group/card grid gap-5 rounded-2xl border border-border bg-card/60 p-5 transition-colors hover:border-primary/50 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:items-start">
      {event.cover_image_url ? (
        <Link
          to="/events/$slug"
          params={{ slug: event.slug }}
          className="block overflow-hidden rounded-xl"
          onClick={() => onOpen?.("image")}
        >
          <img
            src={event.cover_image_url}
            alt={`${event.title} cover`}
            loading="lazy"
            className="aspect-video w-full object-cover transition-transform duration-300 group-hover/card:scale-[1.03]"
          />
        </Link>
      ) : (
        <Link
          to="/events/$slug"
          params={{ slug: event.slug }}
          className="hidden aspect-video w-full place-items-center rounded-xl border border-border bg-secondary/60 sm:grid"
          onClick={() => onOpen?.("image")}
          aria-hidden="true"
          tabIndex={-1}
        >
          <Zap className="size-8 text-primary/50" />
        </Link>
      )}

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {upcoming && <WhenPill event={event} />}
          <TypeChip event={event} />
          {upcoming && <SeatsPill event={event} going={going} />}
          {myStatus && <MyRsvpPill status={myStatus} />}
        </div>
        <h3 className="mt-2 font-display text-xl font-bold">
          <Link
            to="/events/$slug"
            params={{ slug: event.slug }}
            className="transition-colors hover:text-primary"
            onClick={() => onOpen?.("title")}
          >
            {event.title}
          </Link>
        </h3>
        {event.subtitle ? (
          <p className="mt-1 text-sm font-semibold text-foreground">{event.subtitle}</p>
        ) : null}
        <p className="mt-1 text-sm text-primary/90">{eventTypeHook(event.event_type)}</p>
        <EventMeta event={event} />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <HostBadge event={event} />
          {event.is_intro_session && <IntroChip />}
        </div>
        <DescriptionPreview
          event={event}
          max={descriptionMax}
          onOpen={onOpen}
          className={descriptionClassName}
        />
        {cta ? <div className="mt-5">{cta}</div> : null}
      </div>
    </article>
  );
}
